import React from 'react';
import moment from 'moment';

import history from '../../../history';

// Mimics the old admin list design (newadmin/ClientEntry.js): the Metronic
// cartoon avatar, a bold applicant name over the application name, and the
// Last Update / Date Created columns. Rebuilt in Tailwind (admin2 loads no
// Metronic CSS) and navigates into the admin2 detail page at
// /admin/application/:id.
const AVATARS_M = [
    '001-boy.svg', '004-boy-1.svg', '007-boy-2.svg', '008-boy-3.svg', '009-boy-4.svg',
    '011-boy-5.svg', '015-boy-6.svg', '016-boy-7.svg', '024-boy-9.svg', '026-boy-10.svg',
];
const AVATARS_F = [
    '002-girl.svg', '003-girl-1.svg', '005-girl-2.svg', '006-girl-3.svg', '010-girl-4.svg',
    '012-girl-5.svg', '013-girl-6.svg', '014-girl-7.svg', '017-girl-8.svg', '018-girl-9.svg',
];

// Stable hash so an application keeps the same avatar across renders (the old
// component re-randomised on every render — this fixes that flicker).
const hashString = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
};

const pickAvatar = (application) => {
    let list = AVATARS_M;
    const a1 =
        application.completed &&
        application.data &&
        application.data.General_Details &&
        application.data.General_Details.applicant1;
    if (a1 && (a1.title === 'Mrs' || a1.title === 'Ms')) list = AVATARS_F;
    const idx = hashString(application.id || application.name || 'x') % list.length;
    return `${process.env.PUBLIC_URL}/metronic/assets/media/svg/avatars/${list[idx]}`;
};

const renderApplicantNames = (application) => {
    const applicants = application.applicants || {};
    const keys = Object.keys(applicants);
    if (keys.length === 0) return '';
    const names = keys.map((key) => applicants[key].name);
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

const renderLastUpdate = (application) => {
    if (application.last_updated) return moment(application.last_updated).fromNow();
    if (application.created) return moment(application.created).fromNow();
    return undefined;
};

const renderCreated = (application) =>
    application.created ? moment(application.created).format('DD/MM/YYYY') : undefined;

const Avatar = ({ application }) => (
    <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-[#F3F4F6] flex-shrink-0 overflow-hidden">
        <img src={pickAvatar(application)} alt="" className="h-9 w-9 object-contain" />
    </span>
);

const ApplicationRow = ({ application, name, variant = 'row' }) => {
    const openApplication = () => {
        history.push('/admin/application/' + application.id);
    };

    const applicantNames = renderApplicantNames(application);
    const created = renderCreated(application);

    if (variant === 'card') {
        return (
            <button
                type="button"
                onClick={openApplication}
                className="w-full flex flex-col gap-3 px-4 py-4 text-left hover:bg-[#F5F6FA] transition-colors"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <Avatar application={application} />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-[#1F2937] truncate">{applicantNames || name}</p>
                        <p className="text-xs text-[#6B7280] truncate">{application.name}</p>
                    </div>
                    <span className="text-xs text-[#6B7280] whitespace-nowrap">
                        {renderLastUpdate(application) || '-'}
                    </span>
                </div>
            </button>
        );
    }

    return (
        <tr onClick={openApplication} className="cursor-pointer hover:bg-[#F5F6FA] transition-colors group">
            <td className="pl-4 sm:pl-6 pr-4 py-4">
                <div className="flex items-center gap-3 min-w-0">
                    <Avatar application={application} />
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-[#1F2937] truncate group-hover:text-[#7C5CFC] transition-colors">
                            {applicantNames || name}
                        </p>
                        <p className="text-xs text-[#6B7280] truncate">{application.name}</p>
                    </div>
                </div>
            </td>
            <td className="px-4 py-4 whitespace-nowrap">
                <span className="text-sm font-semibold text-[#1F2937]">{renderLastUpdate(application) || '-'}</span>
            </td>
            <td className="pr-4 sm:pr-6 px-4 py-4 whitespace-nowrap">
                <p className="text-sm font-bold text-[#1F2937]">{created || '-'}</p>
                {application.created_by ? <p className="text-xs text-[#6B7280]">{application.created_by}</p> : null}
            </td>
        </tr>
    );
};

// Memoised: the list re-renders on every keystroke and sort click, and each row
// does moment() formatting plus an avatar hash. Props are the application, its
// display name and the variant, so already-mounted rows skip that work entirely.
export default React.memo(ApplicationRow);
